import { BreadcrumbItemType } from '@/components/ui/breadcrumb'
import React from 'react'
import { useSplitView } from '../../../_common/split-view/split-view-context'
import {
  createDatabaseMetadataTab,
  createDatabaseSchemaTab,
  createDatabaseTableTab,
  createDataGovernanceDashboardTab,
  createStaticTab,
} from '../../../_common/split-view/tabs/factories'
import { TabTypeStatic } from '../../../_common/split-view/tabs/types'

/**
 * Navigation functions for opening data catalog tabs
 * Each function creates a new tab or reopens an existing one
 */
export const useDataCatalogNavigation = () => {
  const { addTab } = useSplitView()

  const openDataCatalogTab = React.useCallback(() => {
    addTab({
      tab: createStaticTab(TabTypeStatic.DataCatalog),
    })
  }, [addTab])

  const openSchemaTab = React.useCallback(
    (databaseName?: string, assetType?: string) => {
      addTab({
        tab: createDatabaseSchemaTab({ databaseName: databaseName || '', assetType }),
      })
    },
    [addTab],
  )

  const openTableTab = React.useCallback(
    (databaseName: string, schemaName: string, assetType?: string) => {
      addTab({
        tab: createDatabaseTableTab({ databaseName, schemaName, assetType }),
      })
    },
    [addTab],
  )

  const openMetadataTab = React.useCallback(
    (databaseName: string, schemaName: string, tableName: string, assetType?: string) => {
      addTab({
        tab: createDatabaseMetadataTab({ databaseName, schemaName, tableName, assetType }),
      })
    },
    [addTab],
  )

  const openDataGovernanceDashboardTab = React.useCallback(
    (
      tabView: 'dataQuality' | 'metaData',
      databaseName: string,
      schemaName: string,
      tableName: string,
      assetType?: string,
      sensitivity?: string,
    ) => {
      addTab({
        tab: createDataGovernanceDashboardTab({
          tabView,
          databaseName,
          schemaName,
          tableName,
          assetType,
          sensitivity,
        }),
      })
    },
    [addTab],
  )

  const openIndustryReferenceTab = React.useCallback(() => {
    addTab({
      tab: createStaticTab(TabTypeStatic.IndustryReference),
    })
  }, [addTab])

  return {
    openDataCatalogTab,
    openSchemaTab,
    openTableTab,
    openMetadataTab,
    openDataGovernanceDashboardTab,
    openIndustryReferenceTab,
  }
}

/**
 * Creates breadcrumb navigation items for data catalog pages
 * Returns clickable breadcrumbs that navigate between different data catalog views
 */
export const useDataCatalogBreadcrumbs = (params: {
  database_name?: string
  schema_name?: string
  table_name?: string
  assetType?: string
}) => {
  const { openDataCatalogTab, openSchemaTab, openTableTab } = useDataCatalogNavigation()

  const breadcrumbItems: BreadcrumbItemType[] = React.useMemo(() => {
    const items: BreadcrumbItemType[] = [
      {
        label: 'Data Catalog',
        onClick: openDataCatalogTab,
      },
    ]

    if (params.database_name) {
      items.push({
        label: params.database_name,
        onClick: params.database_name
          ? () => openSchemaTab(params.database_name, params.assetType)
          : undefined,
      })
    }

    if (params.schema_name) {
      items.push({
        label: params.schema_name,
        onClick:
          params.schema_name && params.database_name
            ? () => openTableTab(params.database_name!, params.schema_name!, params.assetType)
            : undefined,
      })
    }

    if (params.table_name) {
      items.push({
        label: params.table_name,
      })
    }

    return items
  }, [
    params.database_name,
    params.schema_name,
    params.table_name,
    params.assetType,
    openDataCatalogTab,
    openSchemaTab,
    openTableTab,
  ])

  return { breadcrumbItems }
}

/**
 * Breadcrumbs for schema page
 * Shows: Data Catalog / Database Name
 */
export const useSchemaTableBreadcrumbs = (database_name: string, assetType?: string) => {
  return useDataCatalogBreadcrumbs({ database_name, assetType })
}

/**
 * Breadcrumbs for table page
 * Shows: Data Catalog / Database Name / Schema Name
 */
export const useTablePageBreadcrumbs = (
  database_name: string,
  schema_name: string,
  assetType?: string,
) => {
  return useDataCatalogBreadcrumbs({ database_name, schema_name, assetType })
}

/**
 * Breadcrumbs for metadata page
 * Shows: Data Catalog / Database Name / Schema Name / Table Name
 */
export const useMetadataTableBreadcrumbs = (
  database_name: string,
  schema_name: string,
  table_name: string,
  assetType?: string,
) => {
  return useDataCatalogBreadcrumbs({ database_name, schema_name, table_name, assetType })
}
